import React, { useMemo } from 'react';
import { GENRE_CATEGORIES, buildGenreStats, isLowCount, isEmpty } from '../utils/genreCategories';

/**
 * GenreFilterBar — shows categories + sub-genre breakdown with counts.
 * Lets staff quickly filter tracks by category/sub-genre and spot gaps
 * (empty or low-count genres are highlighted to know what to fill).
 *
 * Props:
 *  - tracks: array of tracks (each with .genre)
 *  - selectedCategory: string|null
 *  - selectedGenre: string|null (raw genre within the selected category)
 *  - onSelectCategory(catId|null)
 *  - onSelectGenre(rawGenre|null)
 */
export default function GenreFilterBar({
  tracks = [],
  selectedCategory = null,
  selectedGenre = null,
  onSelectCategory,
  onSelectGenre,
}) {
  const { byCategory, total } = useMemo(() => buildGenreStats(tracks), [tracks]);

  const activeCat = selectedCategory && GENRE_CATEGORIES.find(c => c.id === selectedCategory);
  const subGenres = activeCat
    ? Object.entries(byCategory[activeCat.id].genres).sort((a, b) => b[1] - a[1])
    : [];

  return (
    <div className="glass-card p-4 mb-4" data-testid="genre-filter-bar">
      {/* Title row */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-bold">Balance del Catálogo</span>
          <span className="text-[10px] text-white/30">· haz clic en una categoría para filtrar</span>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-white/40">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-400/80" /> Vacío</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-400/80" /> Poco (&lt; 5)</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-400/80" /> OK</span>
        </div>
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => { onSelectCategory?.(null); onSelectGenre?.(null); }}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
            !selectedCategory
              ? 'bg-[#E8720C] text-white border-[#E8720C]'
              : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
          }`}
          data-testid="genre-category-all"
        >
          Todos ({total})
        </button>
        {GENRE_CATEGORIES.map(cat => {
          const count = byCategory[cat.id]?.count ?? 0;
          const active = selectedCategory === cat.id;
          const dot = isEmpty(count)
            ? 'bg-red-400'
            : isLowCount(count)
            ? 'bg-amber-400'
            : 'bg-green-400';
          return (
            <button
              key={cat.id}
              onClick={() => { onSelectCategory?.(cat.id); onSelectGenre?.(null); }}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all flex items-center gap-2 ${
                active
                  ? 'text-white border-white/30'
                  : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
              }`}
              style={active ? { backgroundColor: `${cat.color}26`, borderColor: `${cat.color}80` } : {}}
              data-testid={`genre-category-${cat.id}`}
              title={`${count} tracks en ${cat.label}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
              <span style={active ? { color: cat.color } : {}}>{cat.label}</span>
              <span className="text-white/50">({count})</span>
            </button>
          );
        })}
      </div>

      {/* Sub-genre breakdown */}
      {activeCat && (
        <div className="mt-3 pt-3 border-t border-white/5" data-testid="genre-subgenre-bar">
          <p className="text-[10px] text-white/40 mb-2 uppercase tracking-wider">Géneros en {activeCat.label}</p>
          {subGenres.length === 0 ? (
            <p className="text-xs text-white/30 italic">Sin tracks en esta categoría. Es un buen momento para agregar algunos.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => onSelectGenre?.(null)}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-all ${
                  !selectedGenre ? 'bg-white/10 text-white border-white/20' : 'bg-transparent text-white/50 border-white/10 hover:bg-white/5'
                }`}
                data-testid="subgenre-all"
              >
                Todos ({activeCat && byCategory[activeCat.id].count})
              </button>
              {subGenres.map(([rawGenre, count]) => (
                <button
                  key={rawGenre}
                  onClick={() => onSelectGenre?.(rawGenre)}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-all ${
                    selectedGenre === rawGenre
                      ? 'bg-[#E8720C]/20 text-[#E8720C] border-[#E8720C]/40'
                      : 'bg-transparent text-white/60 border-white/10 hover:bg-white/5 hover:text-white'
                  }`}
                  data-testid={`subgenre-${rawGenre.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {rawGenre} <span className="text-white/40">({count})</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
